'use client';
import {
  Avatar as MUIAvatar,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Typography,
  CardActionArea,
  LinearProgress,
  IconButton,
  alpha,
} from '@mui/material';
import React from 'react';
import { LockRounded, PlayArrowRounded } from '@mui/icons-material';

import { CourseCardProps } from '@/shared/types';
import { DifficultyLevelChip } from '@/shared/ui';
import { getCardColors } from 'src/modules/catalog/utils/common';
import { TeacherEventKeys, TeacherEvents } from '@/teacher';
import { EventBus } from '../utils';
import { routes } from '../utils/routes';
import { adaptCourseTypeText } from '../utils/adapt-course-type-text';
import { formatDateToCustomString } from 'src/modules/teacher/utils/date-format';
import Avatar from './avatars';

export interface UpdatedCourseCardProps extends CourseCardProps {
  eventBus: EventBus<TeacherEvents>;
  isCourseStarted?: boolean; // to indicate if the course is started
  chipLabel?: string; // Nuova proprietà per l'etichetta del chip
  chipColor?: 'primary' | 'secondary' | 'tertiary'; // Nuova proprietà per il colore del chip
  cardClassName?: string;
  date?: string;
  isUpcoming?: boolean;
  linkLive?: string;
}

const CourseCard: React.FC<UpdatedCourseCardProps> = ({
  eventBus,
  documentId,
  imageSrc,
  videoCount,
  coursePercentage,
  title,
  genres = [],
  instructors,
  courseType,
  level,
  highlighted, // Default to false if not provided
  isCourseStarted,
  isLocked,
  cardClassName,
  date,
  isUpcoming,
  linkLive,
}) => {
  // Get the colors based on courseType, highlighted, and isCourseStarted
  const adaptCourseName = courseType?.toLowerCase() === 'percorso' ? 'percorsi' : courseType;
  const cardColors = getCardColors(adaptCourseName, highlighted, isCourseStarted);

  const handleCardClick = () => {
    if (isLocked) {
      eventBus.emit(TeacherEventKeys.redirects.to, {
        url: '/abbonati',
        isExternalPage: false,
      });
      return;
    }

    // Handle upcoming live events that have a link
    if (isUpcoming && linkLive) {
      eventBus.emit(TeacherEventKeys.redirects.to, {
        url: linkLive,
        isExternalPage: true,
      });
      return;
    }

    // Only proceed with normal navigation if the course is not upcoming
    // or if it's a non-masterclass type
    if (!isUpcoming) {
      if (courseType?.toLowerCase() === 'masterclass') {
        eventBus.emit(TeacherEventKeys.redirects.to, {
          url: routes.masterclass.makeUrl(documentId || '', title || ''),
          isExternalPage: false,
        });
        return;
      }
      if (courseType?.toLowerCase() === 'percorso' || courseType?.toLowerCase() === 'percorsi') {
        eventBus.emit(TeacherEventKeys.redirects.to, {
          url: routes.learningPathDetails.makeUrl(documentId || '', title || ''),
          isExternalPage: false,
        });
        return;
      }
      if (courseType?.toLowerCase() === 'serie' || courseType?.toLowerCase() === 'series') {
        eventBus.emit(TeacherEventKeys.redirects.to, {
          url: routes.seriesDetails.makeUrl(documentId || '', title || ''),
          isExternalPage: false,
        });
        return;
      }
    }
  };

  return (
    <Card
      onClick={handleCardClick}
      className={` relative ${cardClassName ? cardClassName : 'min-w-[260px] max-w-[300px]'}`}
      sx={{
        boxShadow: 'none',
        backgroundColor: cardColors?.cardBg,
        color: cardColors?.cardTextColor,
      }}
    >
      {isLocked && (
        <div
          className="absolute z-10 flex flex-col items-center justify-center w-full h-full pointer-events-none inset"
          style={{
            backgroundColor: !highlighted
              ? 'rgba(84,86,104,0.7)'
              : alpha(cardColors?.cardBg || '#ffffff', 0.7),
            backdropFilter: 'blur(2px)',
          }}
        >
          <div className="-translate-y-20 text-center">
            <LockRounded className="h-12" />
            <Typography variant="s1" component="p">
              Abbonati per guardare
            </Typography>
          </div>
        </div>
      )}
      <CardActionArea className="flex flex-col justify-start w-full rounded h-full ">
        <div className="relative w-full">
          <CardMedia
            component="img"
            image={imageSrc || ''}
            alt={title || ''}
            className="object-cover relative w-full h-[180px] md:h-[196px]"
          />
          {!isCourseStarted && courseType && (
            <Chip
              label={
                <Typography variant="button-s" className="capitalize">
                  {adaptCourseTypeText(courseType)}
                </Typography>
              }
              size="small"
              variant="filled"
              className="absolute top-4 left-4 min-h-[24px]"
              sx={{
                backgroundColor: cardColors?.courseTypeChipBg, // Apply chip background color
                color: cardColors?.courseTypeChipTextColor, // Apply chip text color
              }}
            />
          )}
          {courseType?.toLowerCase() !== 'masterclass' &&
            !isCourseStarted &&
            !!videoCount &&
            !isUpcoming && (
              <Chip
                label={
                  <Typography variant="button-s">
                    {`${videoCount} lezion${videoCount !== 1 ? 'i' : 'e'}`}
                  </Typography>
                }
                size="medium"
                className="absolute top-[16px] right-[17px] bg-neutral-main"
                avatar={<MUIAvatar src="/assets/images/play-arrow-filled.png" className="mr-0" />}
              />
            )}
          {/* Show upcoming date instead of video count for upcoming events */}
          {!!date && isUpcoming && (
            <Chip
              avatar={<MUIAvatar src="/assets/images/calendar.png" className="w-6 h-6 mr-0" />}
              label={<Typography variant="button-s">{formatDateToCustomString(date)}</Typography>}
              size="medium"
              className="absolute top-4 right-4 bg-neutral-main"
              sx={{ padding: '0', height: '30px' }}
            />
          )}

          {isCourseStarted && coursePercentage !== undefined && (
            <Chip
              label={`${coursePercentage}%`}
              size="medium"
              className="absolute top-[16px] right-[17px] bg-neutral-main"
              sx={{ padding: '0', height: '30px' }}
            />
          )}
        </div>
        <CardContent
          className="w-full flex flex-col flex-1 justify-between pb-4"
          style={{
            color: cardColors?.cardTextColor, // Apply text color to the card content
          }}
        >
          <div className="space-y-2">
            <div className="flex items-center gap-4">
              <Typography
                variant="h5"
                color={cardColors?.cardTextColor} // Apply text color to the title
                className="gap-2.5 self-stretch w-full my-auto text-left"
              >
                {title}
              </Typography>
              {isCourseStarted && (
                <IconButton
                  size="medium"
                  color="primary"
                  className="button-icon-contained button-icon-primary h-[44px] px-2"
                  sx={{ width: '44px !important' }}
                >
                  <PlayArrowRounded />
                </IconButton>
              )}
            </div>
            {isCourseStarted ? (
              // Show progress bar if course is started
              <div className="py-2">
                <LinearProgress
                  className="mt-2 h-[8px] bg-neutral-main"
                  variant="determinate"
                  value={coursePercentage || 0} // Reflect actual progress
                  sx={{ backgroundColor: '#E2E3E9' }}
                />
              </div>
            ) : (
              // Show difficulty chip, genres, and instructor avatar if course hasn't started
              <div className="flex flex-wrap gap-1">
                <DifficultyLevelChip
                  courseType={courseType}
                  level={level}
                  highlighted={highlighted} // Pass highlighted status
                />
                {Array.isArray(genres) &&
                  genres.map((genre, index) => (
                    <Chip
                      key={index}
                      label={
                        <Typography variant="button-s" sx={{ color: cardColors?.genreChipColor }}>
                          {genre.name}
                        </Typography>
                      }
                      size="small"
                      color="default"
                      variant="outlined"
                      sx={{
                        color: cardColors?.genreChipColor, // Apply text color for genre chip
                        borderColor: cardColors?.genreChipColor, // Apply border color for genre chip
                      }}
                    />
                  ))}
              </div>
            )}
          </div>

          <div className="flex items-center mt-4">
            <Avatar instructors={instructors} courseType={courseType} />
          </div>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default CourseCard;
